/**
 * Matching a `CalendarEvent`'s participant rows against the signed-in
 * identity -- who is "me" on an invite list, and whether my own row is an
 * organiser's or an unanswered invitee's. `calendarAddress` is the only
 * stable key either side carries (`CalendarIdentity.calendarAddress`,
 * `CalendarParticipant.calendarAddress`), compared case-insensitively since
 * an email address is not case-sensitive.
 */
import type {
  CalendarClient,
  CalendarEvent,
  CalendarParticipant,
} from '@wtfalch/calendar/calendar';

/**
 * `CalendarClient.identity()`'s return type, inferred rather than imported:
 * `CalendarIdentity` is defined in the package's `types.ts` and is what
 * `identity()` actually returns, but is missing from `index.ts`'s own
 * export list (`@wtfalch/calendar/calendar`'s public surface stops one
 * type short of what its own method signature needs) --
 * https://github.com/wtfalch/calendar, filed as a Finding rather than
 * fixed here, since this package's source is a different repository.
 */
export type Identity = Awaited<ReturnType<CalendarClient['identity']>>;

function sameAddress(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

export function isMine(participant: CalendarParticipant, identity: Identity | null): boolean {
  return identity !== null && sameAddress(participant.calendarAddress, identity.calendarAddress);
}

export function myParticipant(
  event: Pick<CalendarEvent, 'participants'>,
  identity: Identity | null,
): CalendarParticipant | undefined {
  return event.participants?.find((p) => isMine(p, identity));
}

export function isOrganiser(participant: CalendarParticipant): boolean {
  return participant.roles?.some((r) => r === 'chair' || r === 'owner') ?? false;
}

/** A dashed edge in the week grid: an invitation on my own row that I have
 *  not answered -- never the organiser's own copy of a meeting they made. */
export function isUnanswered(
  event: Pick<CalendarEvent, 'participants'>,
  identity: Identity | null,
): boolean {
  const mine = myParticipant(event, identity);
  if (!mine || isOrganiser(mine)) return false;
  return mine.participationStatus === undefined || mine.participationStatus === 'needs-action';
}
